import React, { useCallback } from "react";
import { UserLoggedInNavigatorNavigationProp } from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { graphql, useMutation } from "react-relay";
import {
  AHText,
  Box,
  Input,
  SectionHeader,
  useBoxStyle,
  useTheme,
} from "arkh";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  NativeStackNavigationProp,
  createNativeStackNavigator,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";

import { OrgStoreCreateMutation } from "./__generated__/OrgStoreCreateMutation.graphql";
import { useNavigationHeader } from "../../../common/navigationHeaderOptions";
import { TimezoneSelector } from "./TimezoneSelector";
import { LanguageSelector } from "./LanguageSelector";
import { FormattedMessage } from "react-intl";
import { ScrollView } from "react-native";
import { PlaceSearchField, SelectedPlace } from "./PlaceSearchField";

type ParamList = {
  OrgStoreCreateScreen: {
    workspaceId: string;
    timezone: string;
  };
};

const Stack = createNativeStackNavigator<ParamList>();

export const OrgStoreCreate = withWebModal(() => {
  const intl_tokens = useIntlTokens();
  const theme = useTheme();
  return (
    <Stack.Navigator>
      <Stack.Screen
        name="OrgStoreCreateScreen"
        component={OrgStoreCreateScreen}
        options={{
          headerTitle: intl_tokens.CreateUnit,
          headerLeft: () => null,
          headerRight: () => null,
          contentStyle: {
            backgroundColor: theme["surface-background-light"],
          },
        }}
      />
    </Stack.Navigator>
  );
});

type OrgStoreCreateScreenProps = {
  route: RouteProp<ParamList, "OrgStoreCreateScreen">;
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"OrgStoreCreate">,
    NativeStackNavigationProp<ParamList, "OrgStoreCreateScreen">
  >;
};

function OrgStoreCreateScreen(props: OrgStoreCreateScreenProps) {
  const intl_tokens = useIntlTokens();
  const [name, setName] = React.useState("");
  const [timezone, setTimezone] = React.useState(props.route.params.timezone);
  const [language, setLanguage] = React.useState("en");
  const [externalId, setExternalId] = React.useState("");
  const [selectedPlace, setSelectedPlace] =
    React.useState<SelectedPlace | null>(null);
  const [adminInviteEmails, setAdminInviteEmails] = React.useState("");
  const [managerInviteEmails, setManagerInviteEmails] = React.useState("");
  const [userInviteEmails, setUserInviteEmails] = React.useState("");
  const [createStore, creatStoreLoading] = useMutation<OrgStoreCreateMutation>(
    graphql`
      mutation OrgStoreCreateMutation(
        $name: String!
        $workspaceId: ID!
        $timezone: String!
        $language: String!
        $externalId: String
        $invites: [StoreInviteEmailBatch!]
        $placeId: String
        $address: String
      ) {
        workspace(id: $workspaceId) {
          createStore(
            storeName: $name
            timezone: $timezone
            locale: $language
            externalId: $externalId
            invites: $invites
            placeId: $placeId
            address: $address
          ) {
            id
            stores {
              id
              name
            }
          }
        }
      }
    `
  );

  const onCreateStore = useCallback(() => {
    createStore({
      variables: {
        name,
        workspaceId: props.route.params.workspaceId,
        timezone,
        language,
        externalId: externalId.length > 0 ? externalId : null,
        placeId: selectedPlace?.placeId ?? null,
        address: selectedPlace?.address ?? null,
        invites: (
          [
            {
              role: "ADMIN",
              emails: adminInviteEmails,
            },
            {
              role: "MANAGER",
              emails: managerInviteEmails,
            },
            {
              role: "CO_WORKER",
              emails: userInviteEmails,
            },
          ] as const
        )
          .map((i) => ({
            role: i.role,
            emails: i.emails
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0),
          }))
          .filter((i) => i.emails.length > 0),
      },
      onCompleted: props.navigation.goBack,
    });
  }, [
    adminInviteEmails,
    createStore,
    externalId,
    language,
    managerInviteEmails,
    name,
    props.navigation.goBack,
    props.route.params.workspaceId,
    selectedPlace,
    timezone,
    userInviteEmails,
  ]);

  useNavigationHeader({
    type: "CREATE",
    onSave: () => onCreateStore(),
    disabled: name.length < 1,
    title: intl_tokens.CreateUnit,
    saveLoading: creatStoreLoading,
    onCancel: () => props.navigation.goBack(),
  });

  const theme = useTheme();
  const contentContainerStyle = useBoxStyle({
    space: "medium",
  });
  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme["surface-background"] }}
      contentContainerStyle={contentContainerStyle}
    >
      <SectionHeader>
        <SectionHeader.Title>{intl_tokens.Name}</SectionHeader.Title>
      </SectionHeader>
      <Input
        autoFocus
        color="surface-primary"
        value={name}
        onChangeText={setName}
        space="medium"
        cornerRadius="large"
        placeholder={intl_tokens.Name}
      />
      <Box spaceTop="large" />
      <SectionHeader>
        <SectionHeader.Title>Region</SectionHeader.Title>
      </SectionHeader>
      <Box style={{ flexDirection: "row", flex: 1, gap: 10 }}>
        <TimezoneSelector
          selectedTimezone={timezone}
          onValueChange={setTimezone}
        />
        <Box spaceTop="large" />
        <LanguageSelector
          selectedLanguage={language}
          onValueChange={setLanguage}
        />
      </Box>
      <Box spaceTop="large" />
      <SectionHeader>
        <SectionHeader.Title>
          <FormattedMessage
            defaultMessage="Address"
            description="OrgStoreCreateScreen address section header"
          />
        </SectionHeader.Title>
      </SectionHeader>
      <PlaceSearchField
        selectedPlace={selectedPlace}
        onSelectPlace={setSelectedPlace}
      />
      <Box spaceTop="large" />
      <SectionHeader>
        <SectionHeader.Title>Metadata</SectionHeader.Title>
      </SectionHeader>
      <Input
        value={externalId}
        onChangeText={setExternalId}
        placeholder="External ID"
      />
      <Box spaceTop="large" />
      <SectionHeader>
        <SectionHeader.Title>{intl_tokens.InviteUser}</SectionHeader.Title>
        <SectionHeader.Description>
          <FormattedMessage
            defaultMessage="Add email addresses to invite users to the store.{br}Separate multiple addresses with commas."
            description="OrgStoreCreateScreen invite user description"
            values={{
              br: "\n",
            }}
          />
        </SectionHeader.Description>
      </SectionHeader>
      <Box spaceTop="medium">
        <AHText spaceHorizontal="medium" spaceBottom="xsmall" type="subtitle">
          Admins
        </AHText>
        <Input
          value={adminInviteEmails}
          multiline
          onChangeText={setAdminInviteEmails}
          placeholder="Admin email address"
        />
      </Box>
      <Box spaceTop="medium">
        <AHText spaceHorizontal="medium" spaceBottom="xsmall" type="subtitle">
          Managers
        </AHText>
        <Input
          value={managerInviteEmails}
          multiline
          onChangeText={setManagerInviteEmails}
          placeholder="Manager email addresses"
        />
      </Box>
      <Box spaceTop="medium">
        <AHText spaceHorizontal="medium" spaceBottom="xsmall" type="subtitle">
          Co-workers
        </AHText>
        <Input
          value={userInviteEmails}
          multiline
          onChangeText={setUserInviteEmails}
          placeholder="Co-worker email addresses"
        />
      </Box>
    </ScrollView>
  );
}
